import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

import { SelectionModel } from '@angular/cdk/collections';


import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { Customer } from '../../../interfaces/customer.interface';
import { Contract } from '../../../interfaces/contract.interface';
import { CallCenterChipComponent } from '../../../shared/components/call-center-chip/call-center-chip.component';

@Component({
  selector: 'app-contracts-list-table',
  imports: [
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatChipsModule,
    CallCenterChipComponent
],
  templateUrl: './contracts-list-table.component.html',
  styleUrl: './contracts-list-table.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ContractsListTableComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() title: string = 'Szerződések Lista';
  @Input() data: Contract[] = [];
  @Input() showHeader: boolean = true;

  constructor(private router: Router) {}

  displayedColumns: string[] = ['insurer', 'product', 'person', 'startDate', 'settlement', 'status', 'premium', 'portfolio', 'expiry'];
  dataSource = new MatTableDataSource<Contract>([]);
  selection = new SelectionModel<Contract>(true, []);

  private scrollListener?: () => void;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit() {
    this.updateDataSource();
  }

  ngAfterViewInit() {
    // Use setTimeout to ensure the view is fully initialized
    setTimeout(() => {
      this.setupTable();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // console.log('ngOnChanges called in table component:', changes);
    if (changes['data']) {
      this.updateDataSource();
    }
  }

  private updateDataSource() {
    // console.log('Updating data source with:', this.data?.length || 0, 'contracts');
    this.dataSource.data = this.data || [];
    
    // Reset paginator to first page when data changes
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  ngOnDestroy() {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
    }
  }

  // Getter for paginated data for mobile view
  get paginatedData(): Contract[] {
    if (!this.paginator) {
      return this.dataSource.filteredData;
    }
    
    const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
    const endIndex = startIndex + this.paginator.pageSize;
    
    return this.dataSource.filteredData.slice(startIndex, endIndex);
  }

  private setupTable() {
    // Set up paginator
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
      // Configure paginator labels in Hungarian
      this.paginator._intl.itemsPerPageLabel = 'Elemek oldalanként:';
      this.paginator._intl.nextPageLabel = 'Következő oldal';
      this.paginator._intl.previousPageLabel = 'Előző oldal';
      this.paginator._intl.firstPageLabel = 'Első oldal';
      this.paginator._intl.lastPageLabel = 'Utolsó oldal';
      
      // Hungarian range label
      this.paginator._intl.getRangeLabel = (page: number, pageSize: number, length: number) => {
        if (length === 0 || pageSize === 0) {
          return `0 / ${length}`;
        }
        const startIndex = page * pageSize + 1;
        const endIndex = Math.min((page + 1) * pageSize, length);
        return `${startIndex} - ${endIndex} / ${length}`;
      };
      
      // Listen to paginator page changes to trigger change detection
      this.paginator.page.subscribe(() => {
        // This will trigger change detection for the paginatedData getter
      });
    }
    
    // Set up sorting
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }

    // Custom filter predicate
    this.dataSource.filterPredicate = (data: Contract, filter: string) => {
      const searchTerm = filter.toLowerCase();
      
      // Search in all relevant fields
      const searchableText = [
        data.insurancecompanyname,
        data.policynumber,
        data.paymentfrequency,
        data.productname,
        data.startdate?.toString(),
        data.policyholdercustomername,
        data.policyholdercompanyname,
        data.insuredcustomername,
        data.insuredcompanyname,
        data.status,
        data.currency,
        data.insurancepremium?.toString(),
        data.annualpremium?.toString(),
        data.dayofarrears?.toString(),
        data.productcode
      ].join(' ').toLowerCase();

      return searchableText.includes(searchTerm);
    };
  }


  private resetPaginator() {
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  // Utility methods
  getStatusLabel(status: string): string {
    return status; // A status már magyar nyelvű
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('hu-HU').format(amount);
  }

  formatPremium(amount: number, currency: string): string {
    if (currency === 'EUR') {
      return new Intl.NumberFormat('hu-HU', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      }).format(amount) + ' €';
    }
    return new Intl.NumberFormat('hu-HU').format(amount) + ' Ft';
  }

  formatPortfolio(amount: number, currency: string): string {
    if (!amount || amount === 0) {
      return '0 ' + (currency === 'EUR' ? '€' : 'Ft');
    }
    if (currency === 'EUR') {
      return new Intl.NumberFormat('hu-HU', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      }).format(amount) + ' €';
    }
    return new Intl.NumberFormat('hu-HU').format(amount) + ' Ft';
  }

  formatDate(date: string | Date): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('hu-HU');
  }

  // Helper method for arrears info formatting
  getArrearsInfo(daysOfArrears: number): string {
    if (daysOfArrears > 0) {
      return `${daysOfArrears} nap hátralék`;
    }
    return 'Nincs hátralék';
  }

  getArrearsClass(daysOfArrears: number): string {
    return daysOfArrears > 0 ? 'has-arrears' : 'no-arrears';
  }

  // Helper method for overdue settlement calculation
  getOverdueSettlementDays(settlementDate: string | Date | null | undefined): number | null {
    if (!settlementDate) return null;
    
    const today = new Date();
    const settlement = new Date(settlementDate);
    
    // Reset time to compare only dates
    today.setHours(0, 0, 0, 0);
    settlement.setHours(0, 0, 0, 0);
    
    // If settlement date is before today, calculate difference in days
    if (settlement < today) {
      const diffTime = today.getTime() - settlement.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    
    return null; // Settlement date is today or in the future
  }

  // Helper method to determine if debt info should be shown
  shouldShowDebtInfo(contract: Contract): boolean {
    return !!(contract.settlementdate && 
           contract.status !== 'törölt' && 
           contract.status !== 'visszavásárolt' &&
           (this.getOverdueSettlementDays(contract.settlementdate) !== null || 
            (contract.dayofarrears && contract.dayofarrears > 0)));
  }

  // Contract status chip configuration
  getContractStatusChip(status: string) {
    return CallCenterChipComponent.createContractStatusChip(status || 'Nincs státusz');
  }

  // Track by function for performance
  trackByIndex(index: number, item: any): number {
    return index;
  }

  // Track by function for contracts
  trackById(index: number, contract: Contract): number {
    return contract.id || index;
  }

  // Navigation methods
  navigateToCustomerDetails(contract: Contract): void {
    // Priority: policyholder first, then insured if no policyholder
    if (contract.policyholdercompanyid && contract.policyholdercompanyname) {
      // Navigate to company details (policyholder)
      this.router.navigate(['/company-details', contract.policyholdercompanyid]);
    } else if (contract.policyholdercustomerid && contract.policyholdercustomername) {
      // Navigate to customer details (policyholder)
      this.router.navigate(['/customer-details', contract.policyholdercustomerid]);
    } else if (contract.insuredcompanyid && contract.insuredcompanyname) {
      // Navigate to company details (insured)
      this.router.navigate(['/company-details', contract.insuredcompanyid]);
    } else if (contract.insuredcustomerid && contract.insuredcustomername) {
      // Navigate to customer details (insured)
      this.router.navigate(['/customer-details', contract.insuredcustomerid]);
    } else {
      console.warn('No valid customer or company ID found for contract:', contract);
    }
  }

  getRowTooltip(contract: Contract): string {
    if (contract.policyholdercompanyid && contract.policyholdercompanyname) {
      return `Kattintson a ${contract.policyholdercompanyname} cég részleteinek megtekintéséhez (szerződő)`;
    } else if (contract.policyholdercustomerid && contract.policyholdercustomername) {
      return `Kattintson ${contract.policyholdercustomername} részleteinek megtekintéséhez (szerződő)`;
    } else if (contract.insuredcompanyid && contract.insuredcompanyname) {
      return `Kattintson a ${contract.insuredcompanyname} cég részleteinek megtekintéséhez (biztosított)`;
    } else if (contract.insuredcustomerid && contract.insuredcustomername) {
      return `Kattintson ${contract.insuredcustomername} részleteinek megtekintéséhez (biztosított)`;
    }
    return 'Nincs elérhető ügyfél információ - sor nem kattintható';
  }
}